/**
 * @file care-agent-config.schema.ts
 * @description Configuração global do agente de jornadas (plano "Agente de
 * Jornadas"). Documento único `careAgentConfig/global`.
 *
 * É o interruptor geral: desliga o agente para TODAS as marcas sem deploy,
 * para incidente de custo ou de fornecedor. Fica fora de `globalAppConfig`
 * de propósito — aquele documento é a tela padrão da Eva, e misturar ali uma
 * chave de infraestrutura confundiria quem edita layout. Também não é
 * variável de ambiente: o script de deploy reescreveria o valor.
 *
 * Documento ausente = agente disponível (o que decide por marca é a
 * funcionalidade `CARE_JOURNEY_AGENT`). A F2 acrescenta aqui o modelo em uso,
 * para que trocar de Gemini para Claude seja configuração.
 */

import { z } from 'zod';
import { CareAgentModeloSchema } from './care-agent-run.schema';

export const CARE_AGENT_CONFIG_ID = 'global';

/**
 * Modelo inicial, decidido em 25/09/2026: Gemini 3.8 Flash no Vertex AI, no
 * endpoint global (o modelo não tem região no Brasil). Se não passar no
 * conjunto de avaliação, a troca para Claude é gravar outro `modelo` aqui.
 */
export const MODELO_PADRAO_DO_AGENTE = { provedor: 'VERTEX_GEMINI' as const, id: 'gemini-3.8-flash', local: 'global' };

export const CareAgentConfigSchema = z.object({
  id: z.literal(CARE_AGENT_CONFIG_ID),
  desligado: z.boolean().default(false),
  /** Por que foi desligado — aparece para quem tenta usar. */
  motivo: z.string().nullable().default(null),
  /** Modelo em uso. Ausente = `MODELO_PADRAO_DO_AGENTE`. */
  modelo: CareAgentModeloSchema.nullable().default(null),
  updatedBy: z.string().nullable().default(null),
  /**
   * Lido direto do Firestore (sem o driver, que converteria), chega como
   * `Timestamp`. `z.coerce.date()` sozinho transforma isso em data inválida,
   * e o documento inteiro passava a ler como corrompido — o agente ficava
   * desligado mesmo depois de religado. Achado rodando o interruptor no
   * emulador, 25/09/2026.
   */
  updatedAt: z.preprocess(
    (v) => (v && typeof (v as any).toDate === 'function' ? (v as any).toDate() : v),
    z.coerce.date().nullable()
  ).default(null),
});
export type CareAgentConfig = z.infer<typeof CareAgentConfigSchema>;
